/**
 * Scheduler Module Exports
 * 
 * Unified export system for the refactored BackgroundSyncScheduler components.
 * Maintains backward compatibility while providing access to modular components.
 */

// Export all base types and interfaces
export type {
  SyncScheduleConfig,
  SyncPriority,
  ScheduleEvent,
  ScheduleEventListener,
  UsagePattern,
  ScheduledSync
} from './BaseScheduler';

export type {
  NetworkConnectionType,
  NetworkQuality,
  NetworkInfo
} from './NetworkAwareScheduler';

// Export individual scheduler classes
export { BaseScheduler } from './BaseScheduler';
export { SyncStrategyScheduler } from './SyncStrategyScheduler';
export { BackgroundTaskScheduler } from './BackgroundTaskScheduler';
export { NetworkAwareScheduler } from './NetworkAwareScheduler';

// Import the complete scheduler for backward compatibility
import { NetworkAwareScheduler, NetworkInfo } from './NetworkAwareScheduler';
import { SyncScheduleConfig, ScheduleEventListener } from './BaseScheduler';
import { BaseScheduler } from './BaseScheduler';
import { SyncStrategyScheduler } from './SyncStrategyScheduler';
import { BackgroundTaskScheduler } from './BackgroundTaskScheduler';

/**
 * Complete Background Sync Scheduler
 * 
 * This class provides the same interface as the original BackgroundSyncScheduler
 * but is now built using the modular architecture. It maintains full backward
 * compatibility while benefiting from the improved structure.
 */
export class BackgroundSyncScheduler extends NetworkAwareScheduler {
  constructor(config: Partial<SyncScheduleConfig> = {}) {
    super(config);
  }

  /**
   * Initialize the scheduler
   * Maintains the same public API as the original implementation
   */
  async initialize(): Promise<void> {
    await super.initialize();
  }

  /**
   * Schedule next sync
   * Maintains the same public API as the original implementation
   */
  scheduleNextSync(priority: 'high' | 'normal' | 'low' = 'normal', reason: string = 'periodic'): void {
    super.scheduleNextSync(priority, reason);
  }

  /**
   * Add high priority sync
   * Maintains the same public API as the original implementation
   */
  scheduleHighPrioritySync(reason: string, data?: unknown): void {
    super.scheduleHighPrioritySync(reason, data);
  }

  /**
   * Update battery level
   * Maintains the same public API as the original implementation
   */
  updateBatteryLevel(level: number): void {
    super.updateBatteryLevel(level);
  }

  /**
   * Record usage activity
   * Maintains the same public API as the original implementation
   */
  recordActivity(): void {
    super.recordActivity();
  }

  /**
   * Get next scheduled sync time
   * Maintains the same public API as the original implementation
   */
  getNextSyncTime(): string | null {
    return super.getNextSyncTime();
  }

  /**
   * Get current interval
   * Maintains the same public API as the original implementation
   */
  getCurrentInterval(): number {
    return super.getCurrentInterval();
  }

  /**
   * Get usage patterns
   * Maintains the same public API as the original implementation
   */
  getUsagePatterns() {
    return super.getUsagePatterns();
  }

  /**
   * Update configuration
   * Maintains the same public API as the original implementation
   */
  updateConfig(updates: Partial<SyncScheduleConfig>): void {
    super.updateConfig(updates);
  }

  /**
   * Get current configuration
   * Maintains the same public API as the original implementation
   */
  getConfig(): SyncScheduleConfig {
    return super.getConfig();
  }

  /**
   * Add event listener
   * Maintains the same public API as the original implementation
   */
  addEventListener(listener: ScheduleEventListener): void {
    super.addEventListener(listener);
  }

  /**
   * Remove event listener
   * Maintains the same public API as the original implementation
   */
  removeEventListener(listener: ScheduleEventListener): void {
    super.removeEventListener(listener);
  }

  /**
   * Cleanup resources
   * Maintains the same public API as the original implementation
   */
  cleanup(): void {
    super.cleanup();
  }

  // Additional methods that were in the original but are now provided by parent classes

  /**
   * Get scheduled syncs (from BackgroundTaskScheduler)
   */
  getScheduledSyncs() {
    return super.getScheduledSyncs();
  }

  /**
   * Process pending syncs (from BackgroundTaskScheduler)
   */
  async processPendingSyncs(): Promise<void> {
    return super.processPendingSyncs();
  }

  /**
   * Cancel scheduled sync (from BackgroundTaskScheduler)
   */
  cancelScheduledSync(syncId: string): boolean {
    return super.cancelScheduledSync(syncId);
  }

  /**
   * Get pending syncs count (from BackgroundTaskScheduler)
   */
  getPendingSyncsCount(): number {
    return super.getPendingSyncsCount();
  }

  /**
   * Get upcoming syncs count (from BackgroundTaskScheduler)
   */
  getUpcomingSyncsCount(): number {
    return super.getUpcomingSyncsCount();
  }

  /**
   * Update network information (from NetworkAwareScheduler)
   */
  updateNetworkInfo(networkInfo: Partial<NetworkInfo>): void {
    super.updateNetworkInfo(networkInfo);
  }

  /**
   * Get network information (from NetworkAwareScheduler)
   */
  getNetworkInfo() {
    return super.getNetworkInfo();
  }

  /**
   * Check if large sync should be performed (from NetworkAwareScheduler)
   */
  shouldPerformLargeSync(estimatedSize: number): boolean {
    return super.shouldPerformLargeSync(estimatedSize);
  }

  /**
   * Get optimized sync strategy (from NetworkAwareScheduler)
   */
  getOptimizedSyncStrategy() {
    return super.getOptimizedSyncStrategy();
  }

  /**
   * Schedule network-optimized sync (from NetworkAwareScheduler)
   */
  async scheduleNetworkOptimizedSync(reason: string): Promise<void> {
    return super.scheduleNetworkOptimizedSync(reason);
  }

  /**
   * Estimate sync data size (from NetworkAwareScheduler)
   */
  async estimateSyncDataSize(): Promise<number> {
    return super.estimateSyncDataSize();
  }
}

// Export singleton instance for backward compatibility
export const backgroundSyncScheduler = new BackgroundSyncScheduler();

/**
 * Factory function to create scheduler instances with specific configurations
 */
export function createBackgroundSyncScheduler(config: Partial<SyncScheduleConfig> = {}): BackgroundSyncScheduler {
  return new BackgroundSyncScheduler(config);
}

/**
 * Factory function to create base scheduler instances
 */
export function createBaseScheduler(config: Partial<SyncScheduleConfig> = {}): BaseScheduler {
  // Since BaseScheduler is abstract, we'll return the concrete implementation
  return new BackgroundSyncScheduler(config);
}

/**
 * Factory function to create strategy scheduler instances
 */
export function createSyncStrategyScheduler(config: Partial<SyncScheduleConfig> = {}): SyncStrategyScheduler {
  return new SyncStrategyScheduler(config);
}

/**
 * Factory function to create background task scheduler instances
 */
export function createBackgroundTaskScheduler(config: Partial<SyncScheduleConfig> = {}): BackgroundTaskScheduler {
  return new BackgroundTaskScheduler(config);
}

/**
 * Factory function to create network-aware scheduler instances
 */
export function createNetworkAwareScheduler(config: Partial<SyncScheduleConfig> = {}): NetworkAwareScheduler {
  return new NetworkAwareScheduler(config);
}

// Re-export everything for convenience
export * from './BaseScheduler';
export * from './SyncStrategyScheduler';
export * from './BackgroundTaskScheduler';
export * from './NetworkAwareScheduler';
